import React from 'react';
import {
  Alert,
  AlertIcon,
  Box,
  Button,
  HStack,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Text,
  VStack,
} from '@chakra-ui/react';
import { Controller } from 'react-hook-form';
import CommonReactSelect from 'components/reactSelectCommon/CommonReactSelect';
import MerchantFilterDropdown from 'components/MerchantFilterDropdown';
import {
  YearOptionsForInvoice,
  QuarterOptionsForInvoice,
  selectDaysOptions,
  languageOptions,
  monthsSelectionsOptions,
} from 'utils/StaticData';
import PropTypes from 'prop-types';

const InvoiceGenerationModal = ({
  isOpen,
  onClose,
  handleSubmit,
  register,
  control,
  watch,
  reset,
  clearErrors,
  setValue,
  errors,
  invoiceMerchantFilterHook,
  modalMerchants,
  showWarning,
  handleGenerate,
  isPDFDownload,
  isEmailSending,
  isGenerating,
  isFormIncomplete,
}) => (
  <Modal
    isOpen={isOpen}
    onClose={onClose}
    isCentered
    closeOnOverlayClick={false}
    size={{ base: 'sm', md: 'md', lg: 'lg' }}
  >
    <ModalOverlay />
    <ModalContent>
      <form onSubmit={handleSubmit(handleGenerate('download'))} noValidate>
        <ModalHeader>Generate Invoices</ModalHeader>
        <ModalCloseButton
          onClick={() => {
            reset();
            clearErrors();
          }}
        />
        <ModalBody>
          <VStack spacing={4} align="stretch">
            {showWarning && (
              <Alert status="warning" borderRadius="md">
                <AlertIcon />
                <Text fontSize="sm">
                  Legal/compliant invoice requires a valid Tax ID (NIF) and full
                  address with postal code.
                </Text>
              </Alert>
            )}
            <MerchantFilterDropdown
              searchTerm={invoiceMerchantFilterHook.searchTerm}
              setSearchTerm={invoiceMerchantFilterHook.setSearchTerm}
              selectedMerchantText={invoiceMerchantFilterHook.getSelectedMerchantText()}
              isPopoverOpen={invoiceMerchantFilterHook.isMerchantPopoverOpen}
              openPopover={invoiceMerchantFilterHook.openMerchantPopover}
              closePopover={invoiceMerchantFilterHook.closeMerchantPopover}
              isDisabled={false}
              merchantNameList={modalMerchants}
              filteredMerchants={invoiceMerchantFilterHook.getFilteredMerchants(
                modalMerchants,
              )}
              pendingSelectedMerchant={
                invoiceMerchantFilterHook.pendingSelectedMerchant
              }
              onSelectAll={invoiceMerchantFilterHook.handleSelectAll}
              onMerchantToggle={invoiceMerchantFilterHook.handleMerchantToggle}
              isSelectAllChecked={invoiceMerchantFilterHook.isSelectAllChecked}
              isMerchantChecked={invoiceMerchantFilterHook.isMerchantChecked}
              onApply={invoiceMerchantFilterHook.handleApplySelection}
              onCancel={invoiceMerchantFilterHook.handleCancelSelection}
              onClearSelection={() =>
                invoiceMerchantFilterHook.handleClearSelection(null, () => {})
              }
              merchantGroups={[]}
              selectedGroup={null}
              isGroupsLoading={false}
              onGroupSelection={() => {}}
              onCreateGroup={() => {}}
              onEditGroup={() => {}}
              showGroups={false}
              showSearch={true}
              showSelectAll={true}
              maxHeight="300px"
              buttonWidth="100%"
            />
            <Controller
              name="year"
              control={control}
              render={({ field: { onChange, value } }) => (
                <CommonReactSelect
                  name="year"
                  type="select"
                  options={YearOptionsForInvoice}
                  value={value ? [value] : []}
                  onChange={onChange}
                  register={register}
                  error={errors?.year}
                  getOptionLabel={(e) => e?.label}
                  getOptionValue={(e) => e?.id}
                  placeholderValue="Year"
                />
              )}
            />
            <Controller
              name="daysSelect"
              control={control}
              render={({ field: { onChange, value } }) => (
                <CommonReactSelect
                  name="daysSelect"
                  type="select"
                  options={selectDaysOptions}
                  value={value ? [value] : []}
                  onChange={(selected) => {
                    onChange(selected);
                    if (selected?.id === 2) {
                      setValue('quarter', null);
                      clearErrors('quarter');
                    } else if (selected?.id === 1) {
                      setValue('month', null);
                      setValue('endMonth', null);
                      clearErrors(['month', 'endMonth']);
                    }
                  }}
                  register={register}
                  error={errors?.daysSelect}
                  getOptionLabel={(e) => e?.label}
                  getOptionValue={(e) => e?.id}
                  placeholderValue="Days"
                />
              )}
            />
            {watch('daysSelect')?.id === 1 ? (
              <Controller
                name="quarter"
                control={control}
                render={({ field: { onChange, value } }) => (
                  <CommonReactSelect
                    label="Quarter"
                    name="quarter"
                    type="select"
                    options={QuarterOptionsForInvoice}
                    value={value ? [value] : []}
                    onChange={onChange}
                    register={register}
                    isRequired={true}
                    error={errors?.quarter}
                    getOptionLabel={(e) => e?.label}
                    getOptionValue={(e) => e?.id}
                  />
                )}
              />
            ) : (
              <HStack spacing={3} align="flex-start">
                <Box flex="1" minW="0">
                  <Controller
                    name="month"
                    control={control}
                    render={({ field: { onChange, value } }) => (
                      <CommonReactSelect
                        label="From Month"
                        name="month"
                        type="select"
                        options={monthsSelectionsOptions}
                        value={value ? [value] : []}
                        onChange={(selected) => {
                          onChange(selected);
                          // RAT-1610: keep the range consecutive, within one year
                          // and <= 6 months. Reset "To" if it now falls outside
                          // [from .. from+5]; default it to "from" (single month).
                          const to = watch('endMonth');
                          if (
                            selected?.id &&
                            (!to?.id ||
                              to.id < selected.id ||
                              to.id > selected.id + 5)
                          ) {
                            setValue('endMonth', selected);
                            clearErrors('endMonth');
                          }
                        }}
                        register={register}
                        isRequired={true}
                        error={errors?.month}
                        getOptionLabel={(e) => e?.label}
                        getOptionValue={(e) => e?.id}
                      />
                    )}
                  />
                </Box>
                <Box flex="1" minW="0">
                  <Controller
                    name="endMonth"
                    control={control}
                    render={({ field: { onChange, value } }) => (
                      <CommonReactSelect
                        label="To Month"
                        name="endMonth"
                        type="select"
                        // Only months from the start month up to 6 consecutive
                        // months are selectable, so a range is always valid.
                        options={monthsSelectionsOptions.filter(
                          (m) =>
                            !watch('month')?.id ||
                            (m.id >= watch('month').id &&
                              m.id <= watch('month').id + 5),
                        )}
                        value={value ? [value] : []}
                        onChange={onChange}
                        register={register}
                        isRequired={true}
                        isDisabled={!watch('month')?.id}
                        error={errors?.endMonth}
                        getOptionLabel={(e) => e?.label}
                        getOptionValue={(e) => e?.id}
                      />
                    )}
                  />
                </Box>
              </HStack>
            )}
            <Controller
              name="language"
              control={control}
              render={({ field: { onChange, value } }) => (
                <CommonReactSelect
                  label="Language"
                  name="language"
                  type="select"
                  options={languageOptions}
                  value={value ? [value] : []}
                  onChange={onChange}
                  register={register}
                  error={errors?.language}
                  getOptionLabel={(e) => e?.label}
                  getOptionValue={(e) => e?.id}
                  placeholderValue="Language"
                />
              )}
            />
          </VStack>
        </ModalBody>
        <ModalFooter>
          <Button
            type="button"
            bg="primary"
            color="white"
            _hover={{ bg: 'primary', opacity: 0.8 }}
            mr={3}
            disabled={
              isEmailSending ||
              isPDFDownload ||
              isGenerating ||
              isFormIncomplete
            }
            isLoading={isGenerating}
            loadingText="Generating..."
            fontSize="sm"
            fontWeight="500"
            onClick={handleSubmit(handleGenerate('generate'))}
          >
            Generate
          </Button>
          {/*
            RAT-1712: "Send Mail" is temporarily hidden so invoices aren't
            accidentally emailed to all merchants. No logic removed — restore
            the button by un-commenting this block.
          <Button
            type="button"
            bg="primary"
            color="white"
            _hover={{ bg: 'primary', opacity: 0.8 }}
            mr={3}
            disabled={isEmailSending || isPDFDownload || isFormIncomplete}
            isLoading={isEmailSending}
            loadingText="Sending..."
            fontSize="sm"
            fontWeight="500"
            onClick={handleSubmit(handleGenerate('email'))}
          >
            Send Mail
          </Button>
          */}
          <Button
            type="button"
            bg="primary"
            color="white"
            _hover={{ bg: 'primary', opacity: 0.8 }}
            mr={3}
            disabled={isEmailSending || isPDFDownload}
            isLoading={isPDFDownload}
            loadingText="Generating..."
            fontSize="sm"
            fontWeight="500"
            onClick={handleSubmit(handleGenerate('download'))}
          >
            Download Invoice
          </Button>
        </ModalFooter>
      </form>
    </ModalContent>
  </Modal>
);

InvoiceGenerationModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  handleSubmit: PropTypes.func.isRequired,
  register: PropTypes.func.isRequired,
  control: PropTypes.object.isRequired,
  watch: PropTypes.func.isRequired,
  reset: PropTypes.func.isRequired,
  clearErrors: PropTypes.func.isRequired,
  setValue: PropTypes.func.isRequired,
  errors: PropTypes.object.isRequired,
  invoiceMerchantFilterHook: PropTypes.object.isRequired,
  modalMerchants: PropTypes.array.isRequired,
  showWarning: PropTypes.bool.isRequired,
  handleGenerate: PropTypes.func.isRequired,
  isPDFDownload: PropTypes.bool.isRequired,
  isEmailSending: PropTypes.bool.isRequired,
  isGenerating: PropTypes.bool.isRequired,
  isFormIncomplete: PropTypes.bool.isRequired,
};

export default InvoiceGenerationModal;
